import type { ChronologySettingsViewModel } from './chronology.js';
import type { DataManagementControllerSnapshot } from './dataManagementController.js';
import { buildAppShellViewModel, type AppShellViewModel } from './appShellPresentation.js';
import { buildBirthInputFormViewModel, type BirthInputFormViewModel } from './birthInputPresentation.js';
import type { ProductionAppSnapshot } from './productionAppController.js';
import type { ProductionChartPageViewModel } from './productionChartPagePresentation.js';
import type { ProductionNatalResultPageViewModel } from './productionResultPresentation.js';
import type { ProductionTimingPageViewModel } from './productionTimingPresentation.js';
import type { RelationshipReportViewModel } from './relationshipPresentation.js';

export interface RelationshipInputPageViewModel {
  model: 'RELATIONSHIP-INPUT-PAGE-0.1.0';
  personA: BirthInputFormViewModel;
  personB: BirthInputFormViewModel;
  relationshipTypes: readonly { value: 'partner' | 'siblings' | 'parent_to_child' | 'child_to_parent'; label: string }[];
  disclosure: string;
}

export interface TimingInputPageViewModel {
  model: 'TIMING-INPUT-PAGE-0.1.0';
  disclosure: string;
}

export interface ProductionScreenRouterIntegrations {
  viewport?: 'desktop' | 'mobile';
  dataManagement?: DataManagementControllerSnapshot;
  chronologySettings?: ChronologySettingsViewModel;
}

export type ProductionReadyScreenPayload =
  | BirthInputFormViewModel
  | ProductionChartPageViewModel
  | ProductionNatalResultPageViewModel
  | ProductionTimingPageViewModel
  | TimingInputPageViewModel
  | RelationshipInputPageViewModel
  | RelationshipReportViewModel
  | DataManagementControllerSnapshot
  | ChronologySettingsViewModel;

export interface ProductionScreenReadyViewModel {
  status: 'ready';
  screenId: ProductionAppSnapshot['navigation']['activeScreen'];
  shell: AppShellViewModel;
  payload: ProductionReadyScreenPayload;
}

export interface ProductionScreenPendingViewModel {
  status: 'integration_pending';
  screenId: ProductionAppSnapshot['navigation']['activeScreen'];
  shell: AppShellViewModel;
  reason: string;
}

export type ProductionScreenViewModel = ProductionScreenReadyViewModel | ProductionScreenPendingViewModel;

function relationshipInputPage(): RelationshipInputPageViewModel {
  return Object.freeze({
    model: 'RELATIONSHIP-INPUT-PAGE-0.1.0' as const,
    personA: buildBirthInputFormViewModel('solar'),
    personB: buildBirthInputFormViewModel('solar'),
    relationshipTypes: Object.freeze([
      Object.freeze({ value: 'partner' as const, label: '伴侶／夫妻' }),
      Object.freeze({ value: 'siblings' as const, label: '兄弟姊妹' }),
      Object.freeze({ value: 'parent_to_child' as const, label: '父母 → 子女' }),
      Object.freeze({ value: 'child_to_parent' as const, label: '子女 → 父母' }),
    ]),
    disclosure: '雙人分析只在瀏覽器本機處理兩份出生資料；父母／子女目前仍以 Research Pending 為主，不用高風險古籍斷語硬補結論。',
  });
}

function timingInputPage(): TimingInputPageViewModel {
  return Object.freeze({
    model: 'TIMING-INPUT-PAGE-0.1.0' as const,
    disclosure: '\u8acb\u9078\u64c7\u76ee\u6a19\u65e5\u671f\u8207\u6642\u8fb0\u5f8c\u5728\u700f\u89bd\u5668\u672c\u6a5f\u8a08\u7b97\u904b\u9650\uff1b\u5c1a\u672a\u8a08\u7b97\u524d\u4e0d\u88fd\u9020\u4efb\u4f55\u904b\u52e2\u7d50\u8ad6\u3002',
  });
}

function pending(snapshot: ProductionAppSnapshot, shell: AppShellViewModel, reason: string): ProductionScreenPendingViewModel {
  return Object.freeze({ status: 'integration_pending' as const, screenId: snapshot.navigation.activeScreen, shell, reason });
}

function ready(snapshot: ProductionAppSnapshot, shell: AppShellViewModel, payload: ProductionReadyScreenPayload): ProductionScreenReadyViewModel {
  return Object.freeze({ status: 'ready' as const, screenId: snapshot.navigation.activeScreen, shell, payload });
}

export function routeProductionScreen(
  snapshot: ProductionAppSnapshot,
  integrations: ProductionScreenRouterIntegrations = {},
): ProductionScreenViewModel {
  const shell = buildAppShellViewModel(snapshot.navigation, integrations.viewport ?? 'desktop');
  switch (snapshot.navigation.activeScreen) {
    case 'birth_input': return ready(snapshot, shell, buildBirthInputFormViewModel('solar'));
    case 'natal_chart': return snapshot.natalChart ? ready(snapshot, shell, snapshot.natalChart) : pending(snapshot, shell, '尚無可呈現的十二宮命盤。');
    case 'natal_interpretation': return snapshot.natalResult ? ready(snapshot, shell, snapshot.natalResult) : pending(snapshot, shell, '尚無可呈現的本命解讀。');
    case 'natal_timing': return snapshot.timingResult ? ready(snapshot, shell, snapshot.timingResult) : snapshot.navigation.natalResultAvailable ? ready(snapshot, shell, timingInputPage()) : pending(snapshot, shell, '\u8acb\u5148\u5b8c\u6210\u672c\u547d\u6392\u76e4\u3002');
    case 'relationship': return ready(snapshot, shell, relationshipInputPage());
    case 'relationship_result': return snapshot.relationshipResult ? ready(snapshot, shell, snapshot.relationshipResult) : pending(snapshot, shell, '尚無可呈現的雙人分析結果。');
    case 'data_management': return integrations.dataManagement ? ready(snapshot, shell, integrations.dataManagement) : pending(snapshot, shell, '資料管理 Controller 尚未注入目前畫面。');
    case 'settings': return integrations.chronologySettings ? ready(snapshot, shell, integrations.chronologySettings) : pending(snapshot, shell, 'Chronology Settings 尚未注入目前畫面。');
  }
}
